import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Search,
  Package,
  Palette,
  Ruler,
  Tag,
  X,
  Check,
  ChevronDown,
  Filter,
  Grid3X3,
  List,
  SlidersHorizontal
} from 'lucide-react';

interface ProductColor {
  id: string;
  name: string;
  hex_code?: string;
}

interface ProductSize {
  id: string;
  name: string;
  sort_order: number;
}

interface InventoryItem {
  id: string;
  name: string;
  quantity: number;
  category?: string;
  location?: string;
  sku?: string;
  barcode?: string;
  reference_code?: string;
  color_id?: string;
  size_id?: string;
}

interface ItemPickerProps {
  items: InventoryItem[];
  colors: ProductColor[];
  sizes: ProductSize[];
  allCategories?: string[];
  selectedItemId: string | null;
  excludedItemIds?: Set<string>;
  onSelect: (item: InventoryItem) => void;
  onClear: () => void;
  inlineMode?: boolean;
}

type ViewMode = 'list' | 'grid';

export const ItemPicker: React.FC<ItemPickerProps> = ({
  items,
  colors,
  sizes,
  allCategories,
  selectedItemId,
  excludedItemIds = new Set(),
  onSelect,
  onClear,
  inlineMode = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedColorId, setSelectedColorId] = useState<string | null>(null);
  const [selectedSizeId, setSelectedSizeId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showFilters, setShowFilters] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const getColorName = useCallback((colorId?: string) =>
    colors.find(c => c.id === colorId)?.name || '', [colors]);

  const getColorHex = useCallback((colorId?: string) =>
    colors.find(c => c.id === colorId)?.hex_code || '', [colors]);

  const getSizeName = useCallback((sizeId?: string) =>
    sizes.find(s => s.id === sizeId)?.name || '', [sizes]);

  const categories = useMemo(() => {
    if (allCategories && allCategories.length > 0) {
      return allCategories;
    }
    const cats = new Set<string>();
    items.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [items, allCategories]);

  const availableItems = useMemo(() =>
    items.filter(item => !excludedItemIds.has(item.id)), [items, excludedItemIds]);

  const filteredItems = useMemo(() => {
    let baseItems = availableItems.filter(item => {
      if (selectedCategory && item.category !== selectedCategory) return false;
      if (selectedColorId && item.color_id !== selectedColorId) return false;
      if (selectedSizeId && item.size_id !== selectedSizeId) return false;
      return true;
    });

    if (!searchTerm) return baseItems;

    const searchLower = searchTerm.toLowerCase().trim();

    const exactRefMatch = baseItems.filter(item =>
      item.reference_code && item.reference_code.toLowerCase() === searchLower
    );
    if (exactRefMatch.length > 0) return exactRefMatch;

    const exactSkuMatch = baseItems.filter(item =>
      item.sku && item.sku.toLowerCase() === searchLower
    );
    if (exactSkuMatch.length > 0) return exactSkuMatch;

    const exactBarcodeMatch = baseItems.filter(item =>
      item.barcode && item.barcode.toLowerCase() === searchLower
    );
    if (exactBarcodeMatch.length > 0) return exactBarcodeMatch;

    return baseItems.filter(item => {
      const colorName = getColorName(item.color_id);
      const sizeName = getSizeName(item.size_id);

      return (
        item.name.toLowerCase().includes(searchLower) ||
        (item.category && item.category.toLowerCase().includes(searchLower)) ||
        (colorName && colorName.toLowerCase().includes(searchLower)) ||
        (sizeName && sizeName.toLowerCase().includes(searchLower)) ||
        (item.sku && item.sku.toLowerCase().includes(searchLower)) ||
        (item.barcode && item.barcode.toLowerCase().includes(searchLower)) ||
        (item.reference_code && item.reference_code.toLowerCase().includes(searchLower))
      );
    });
  }, [availableItems, searchTerm, selectedCategory, selectedColorId, selectedSizeId, getColorName, getSizeName]);

  const selectedItem = useMemo(() =>
    items.find(item => item.id === selectedItemId), [items, selectedItemId]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedCategory) count++;
    if (selectedColorId) count++;
    if (selectedSizeId) count++;
    return count;
  }, [selectedCategory, selectedColorId, selectedSizeId]);

  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategory(null);
    setSelectedColorId(null);
    setSelectedSizeId(null);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelectItem = useCallback((item: InventoryItem) => {
    onSelect(item);
    setIsOpen(false);
    clearAllFilters();
  }, [onSelect, clearAllFilters]);

  const renderSelectedDisplay = () => {
    if (!selectedItem) {
      return (
        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
          <Package className="h-5 w-5" />
          <span className="text-sm">Click to select an item...</span>
        </div>
      );
    }

    const colorName = getColorName(selectedItem.color_id);
    const colorHex = getColorHex(selectedItem.color_id);
    const sizeName = getSizeName(selectedItem.size_id);

    return (
      <div className="flex items-center justify-between gap-3 w-full">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="h-4 w-4 text-teal-600 dark:text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {selectedItem.name}
              {selectedItem.reference_code && (
                <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal">{selectedItem.reference_code}</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {colorName && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  {colorHex && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorHex }} />}
                  {colorName}
                </span>
              )}
              {sizeName && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  {sizeName}
                </span>
              )}
              {selectedItem.category && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                  {selectedItem.category}
                </span>
              )}
              <span className="px-1.5 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                {selectedItem.quantity}
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>
    );
  };

  const renderListItem = (item: InventoryItem) => {
    const colorName = getColorName(item.color_id);
    const colorHex = getColorHex(item.color_id);
    const sizeName = getSizeName(item.size_id);
    const isSelected = item.id === selectedItemId;

    return (
      <div
        key={item.id}
        onClick={() => handleSelectItem(item)}
        className={`group px-5 py-4 cursor-pointer transition-all duration-150 ${
          isSelected
            ? 'bg-teal-50 dark:bg-teal-900/20 border-l-4 border-l-teal-500'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 border-l-transparent'
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <Package className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-base text-gray-900 dark:text-gray-100 truncate">
                  {item.name}
                  {item.reference_code && (
                    <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal text-sm">{item.reference_code}</span>
                  )}
                </span>
                {isSelected && (
                  <Check className="h-5 w-5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {colorName && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                    {colorHex && <span className="w-3 h-3 rounded-full border border-amber-300 dark:border-amber-600" style={{ backgroundColor: colorHex }} />}
                    {colorName}
                  </span>
                )}
                {sizeName && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                    <Ruler className="h-3 w-3" />
                    {sizeName}
                  </span>
                )}
                {item.category && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                    <Tag className="h-3 w-3" />
                    {item.category}
                  </span>
                )}
                {item.location && (
                  <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                    {item.location}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right flex-shrink-0 pl-4">
            <div className={`text-2xl font-bold ${item.quantity <= 10 ? 'text-red-600 dark:text-red-400' : item.quantity <= 25 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'}`}>
              {item.quantity}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">in stock</div>
          </div>
        </div>
      </div>
    );
  };

  const renderGridItem = (item: InventoryItem) => {
    const colorName = getColorName(item.color_id);
    const colorHex = getColorHex(item.color_id);
    const sizeName = getSizeName(item.size_id);
    const isSelected = item.id === selectedItemId;

    return (
      <div
        key={item.id}
        onClick={() => handleSelectItem(item)}
        className={`group p-4 cursor-pointer transition-all duration-150 rounded-xl border-2 ${
          isSelected
            ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-500 shadow-md'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-lg'
        }`}
      >
        <div className="flex items-center justify-center mb-3">
          <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <Package className="h-7 w-7 text-gray-400 dark:text-gray-500" />
          </div>
        </div>
        <div className="text-center mb-3">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 line-clamp-2 leading-tight">
              {item.name}
            </span>
            {isSelected && (
              <Check className="h-4 w-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
            )}
          </div>
          {item.reference_code && (
            <span className="text-xs text-gray-500 dark:text-gray-400 block">
              {item.reference_code}
            </span>
          )}
          {item.category && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {item.category}
            </span>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-1.5 mb-3">
          {colorName && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
              {colorHex && <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorHex }} />}
              {colorName}
            </span>
          )}
          {sizeName && (
            <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
              {sizeName}
            </span>
          )}
        </div>
        <div className="text-center pt-2 border-t border-gray-100 dark:border-gray-700">
          <span className={`text-xl font-bold ${item.quantity <= 10 ? 'text-red-600 dark:text-red-400' : item.quantity <= 25 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {item.quantity}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">in stock</span>
        </div>
      </div>
    );
  };

  const renderInlineContent = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-3 bg-gray-50 dark:bg-gray-800/50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, SKU, reference..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-gray-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
            >
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              showFilters || activeFiltersCount > 0
                ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="px-1.5 py-0.5 bg-teal-600 text-white text-xs rounded-full min-w-[18px] text-center">
                {activeFiltersCount}
              </span>
            )}
          </button>

          <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-gray-100'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-gray-100'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Grid3X3 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            {categories.length > 0 && (
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                  <Tag className="h-3 w-3" /> Category
                </span>
                <div className="flex flex-wrap gap-1">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {colors.length > 0 && (
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                  <Palette className="h-3 w-3" /> Color
                </span>
                <div className="flex flex-wrap gap-1">
                  {colors.map(color => (
                    <button
                      key={color.id}
                      onClick={() => setSelectedColorId(selectedColorId === color.id ? null : color.id)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                        selectedColorId === color.id
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {color.hex_code && (
                        <span
                          className="w-2.5 h-2.5 rounded-full border border-gray-300 dark:border-gray-500"
                          style={{ backgroundColor: color.hex_code }}
                        />
                      )}
                      {color.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sizes.length > 0 && (
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                  <Ruler className="h-3 w-3" /> Size
                </span>
                <div className="flex flex-wrap gap-1">
                  {sizes.map(size => (
                    <button
                      key={size.id}
                      onClick={() => setSelectedSizeId(selectedSizeId === size.id ? null : size.id)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        selectedSizeId === size.id
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {size.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {filteredItems.length} items
        </span>
        {excludedItemIds.size > 0 && (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            {excludedItemIds.size} in shipment
          </span>
        )}
      </div>

      <div className="overflow-y-auto h-[400px] lg:h-[500px]">
        {filteredItems.length === 0 ? (
          <div className="p-8 text-center h-full flex flex-col items-center justify-center">
            <Filter className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">No items found</p>
            {activeFiltersCount > 0 && (
              <button onClick={clearAllFilters} className="mt-2 text-xs text-teal-600 dark:text-teal-400 hover:underline">
                Clear filters
              </button>
            )}
          </div>
        ) : viewMode === 'list' ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {filteredItems.map(item => renderCompactListItem(item))}
          </div>
        ) : (
          <div className="p-2 grid grid-cols-2 gap-2">
            {filteredItems.map(item => renderCompactGridItem(item))}
          </div>
        )}
      </div>
    </div>
  );

  const renderCompactListItem = (item: InventoryItem) => {
    const colorName = getColorName(item.color_id);
    const colorHex = getColorHex(item.color_id);
    const sizeName = getSizeName(item.size_id);
    const isSelected = item.id === selectedItemId;

    return (
      <div
        key={item.id}
        onClick={() => onSelect(item)}
        className={`px-3 py-2.5 cursor-pointer transition-all ${
          isSelected
            ? 'bg-teal-50 dark:bg-teal-900/20 border-l-3 border-l-teal-500'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                {item.name}
                {item.reference_code && (
                  <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal text-xs">{item.reference_code}</span>
                )}
              </span>
              {isSelected && <Check className="h-4 w-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />}
            </div>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {item.sku && (
                <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                  {item.sku}
                </span>
              )}
              {colorName && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  {colorHex && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorHex }} />}
                  {colorName}
                </span>
              )}
              {sizeName && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  {sizeName}
                </span>
              )}
              {item.category && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                  {item.category}
                </span>
              )}
            </div>
          </div>
          <div className={`text-sm font-bold flex-shrink-0 ${item.quantity <= 10 ? 'text-red-600 dark:text-red-400' : item.quantity <= 25 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
            {item.quantity}
          </div>
        </div>
      </div>
    );
  };

  const renderCompactGridItem = (item: InventoryItem) => {
    const colorName = getColorName(item.color_id);
    const colorHex = getColorHex(item.color_id);
    const sizeName = getSizeName(item.size_id);
    const isSelected = item.id === selectedItemId;

    return (
      <div
        key={item.id}
        onClick={() => onSelect(item)}
        className={`p-2.5 cursor-pointer transition-all rounded-lg border ${
          isSelected
            ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-500'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600'
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="font-medium text-xs text-gray-900 dark:text-gray-100 truncate">
            {item.name}
          </span>
          {isSelected && <Check className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400 flex-shrink-0" />}
        </div>
        {item.reference_code && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 truncate">
            {item.reference_code}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mb-1.5">
          {item.sku && (
            <span className="px-1 py-0.5 rounded text-[10px] font-mono bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
              {item.sku}
            </span>
          )}
          {colorName && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
              {colorHex && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorHex }} />}
              {colorName}
            </span>
          )}
          {sizeName && (
            <span className="px-1 py-0.5 rounded text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              {sizeName}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{item.category}</span>
          <span className={`text-xs font-bold ${item.quantity <= 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
            {item.quantity}
          </span>
        </div>
      </div>
    );
  };

  if (inlineMode) {
    return renderInlineContent();
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Select Existing Item
      </label>

      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`min-h-[52px] px-4 py-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
          isOpen
            ? 'border-teal-500 ring-2 ring-teal-500/20 bg-white dark:bg-gray-800'
            : selectedItem
              ? 'border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/10 hover:border-teal-300 dark:hover:border-teal-700'
              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {renderSelectedDisplay()}
          </div>
          {!selectedItem && (
            <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-3 bg-gray-50 dark:bg-gray-800/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, SKU, barcode, reference, color, size..."
                className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilters(!showFilters);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showFilters || activeFiltersCount > 0
                    ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-teal-600 text-white text-xs rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMode('list');
                  }}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  title="List view"
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMode('grid');
                  }}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  title="Grid view"
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                      <Tag className="h-3.5 w-3.5" />
                      Category
                    </span>
                    {selectedCategory && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategory(null);
                        }}
                        className="text-xs text-teal-600 dark:text-teal-400 hover:underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategory(selectedCategory === category ? null : category);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          selectedCategory === category
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                {colors.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                        <Palette className="h-3.5 w-3.5" />
                        Color
                      </span>
                      {selectedColorId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedColorId(null);
                          }}
                          className="text-xs text-teal-600 dark:text-teal-400 hover:underline"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {colors.map(color => (
                        <button
                          key={color.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedColorId(selectedColorId === color.id ? null : color.id);
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            selectedColorId === color.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                          }`}
                        >
                          {color.hex_code && (
                            <span
                              className="w-3 h-3 rounded-full border border-white/30"
                              style={{ backgroundColor: color.hex_code }}
                            />
                          )}
                          {color.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {sizes.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                        <Ruler className="h-3.5 w-3.5" />
                        Size
                      </span>
                      {selectedSizeId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSizeId(null);
                          }}
                          className="text-xs text-teal-600 dark:text-teal-400 hover:underline"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {sizes.map(size => (
                        <button
                          key={size.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSizeId(selectedSizeId === size.id ? null : size.id);
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            selectedSizeId === size.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                          }`}
                        >
                          {size.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {activeFiltersCount > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAllFilters();
                    }}
                    className="w-full py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} found
              {activeFiltersCount > 0 && ` (filtered from ${availableItems.length})`}
            </span>
            {excludedItemIds.size > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                {excludedItemIds.size} already in shipment
              </span>
            )}
          </div>

          <div className="overflow-y-auto min-h-[400px] max-h-[70vh]">
            {filteredItems.length === 0 ? (
              <div className="p-12 text-center min-h-[400px] flex flex-col items-center justify-center">
                <Filter className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" />
                <p className="text-lg text-gray-500 dark:text-gray-400 font-medium">No items found</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                  Try adjusting your search or filters
                </p>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAllFilters();
                    }}
                    className="mt-4 px-4 py-2 text-sm font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : viewMode === 'list' ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredItems.map(renderListItem)}
              </div>
            ) : (
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredItems.map(renderGridItem)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
